/**
 * @author        matthew@zumper.com (Matthew Talamini)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   LinkPackLink
 */

import {
  Button,
  Link as ChakraLink,
  Flex,
  type FlexProps,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react'
import { locationDescriptorToString } from '@zcode/nr-utils'
import { useCallback } from 'react'
import { Link as ReactRouterDomLink, useHistory } from 'react-router-dom'
import type { HandleLinkClickArgs, LinkObject } from './types'

export interface LinkPackLinkProps extends FlexProps {
  analyticChannel?: string
  asButton?: boolean
  handleLinkClick?: (args: HandleLinkClickArgs) => void
  header?: string
  link: LinkObject
  oneColumn?: boolean
  showDetailColumn?: boolean
  testId?: string
}

export const LinkPackLink = ({
  analyticChannel,
  asButton,
  handleLinkClick,
  header,
  link,
  oneColumn,
  showDetailColumn,
  testId,
  ...rest
}: LinkPackLinkProps) => {
  const history = useHistory()

  const handleClick = useCallback(() => {
    if (asButton) {
      const url = link.href ?? (link.to as string | undefined)
      window.open(url, '_blank')
    }
    if (link.eventName && link.linkName && analyticChannel) {
      handleLinkClick?.({
        analyticChannel,
        section: header,
        eventName: link.eventName,
        link: locationDescriptorToString(link.linkName, history),
      })
    }
  }, [
    analyticChannel,
    asButton,
    handleLinkClick,
    header,
    history,
    link.eventName,
    link.href,
    link.linkName,
  ])
  return (
    <Flex
      align="center"
      data-testid={testId && `${testId}-grid`}
      flex={
        oneColumn
          ? 1
          : {
              base: '0 0 100%',
              sm: '0 0 calc(50% - 12px)',
              // removing these for now so that it is up to two columns; we can revise if needed for full width city guide
              // lg: '0 0 calc(33% - 16px)',
              // xl: '0 0 calc(25% - 18px)',
            }
      }
      gap={5}
      justify="space-between"
      {...rest}
    >
      {asButton ? (
        <Button
          _hover={{ color: 'accent', textDecoration: 'underline' }}
          background="none"
          color="accent"
          fontSize="md"
          fontWeight="regular"
          letterSpacing="tight"
          lineHeight={2.25}
          noOfLines={1}
          onClick={handleClick}
          p={0}
          textDecoration="none"
        >
          {link.title}
          {showDetailColumn && link.details && <VisuallyHidden>{link.details}</VisuallyHidden>}
        </Button>
      ) : (
        <ChakraLink
          _hover={{ color: 'accent', textDecoration: 'underline' }}
          as={link.to ? ReactRouterDomLink : 'a'}
          color="accent"
          fontSize="md"
          href={link.href}
          letterSpacing="tight"
          lineHeight={2.25}
          noOfLines={1}
          onClick={handleClick}
          pr={3}
          target="_blank"
          textDecoration="none"
          // @ts-expect-error
          to={link.to}
        >
          {link.title}
          {showDetailColumn && link.details && <VisuallyHidden>{link.details}</VisuallyHidden>}
        </ChakraLink>
      )}
      {showDetailColumn && link.details && (
        <Text flexShrink={0} noOfLines={1}>
          {link.details}
        </Text>
      )}
    </Flex>
  )
}
